import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  ogImageAlt?: string;
  author?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  schema?: object;
  noIndex?: boolean;
}

const SITE_URL = 'https://roseville.org';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/426e02c761dfd1ba811e7874fd57a840.jpg';

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  author,
  articlePublishedTime,
  articleModifiedTime,
  schema,
  noIndex = false,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  const fullUrl = canonical
    ? `${siteUrl}${canonical}`
    : `${siteUrl}${location.pathname}`;
  const imageUrl = ogImage || DEFAULT_OG_IMAGE;
  const imageAlt = ogImageAlt || 'Roseville, California — Community Guide';

  useEffect(() => {
    document.title = title;

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Basic
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    
    // Author
    if (author) {
      updateMeta('author', author);
    }

    // Open Graph
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:type', ogType, true);
    updateMeta('og:url', fullUrl, true);
    updateMeta('og:image', imageUrl, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:image:alt', imageAlt, true);
    updateMeta('og:site_name', 'Roseville.org', true);
    updateMeta('og:locale', 'en_US', true);

    // Article-specific Open Graph tags
    if (articlePublishedTime) {
      updateMeta('article:published_time', articlePublishedTime, true);
    }
    if (articleModifiedTime) {
      updateMeta('article:modified_time', articleModifiedTime, true);
    }

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', imageUrl);
    updateMeta('twitter:image:alt', imageAlt);
    updateMeta('twitter:site', '@RosevilleOrg');

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = fullUrl;

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMeta('last-modified', today);

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[data-seo="page"][type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        schemaScript.setAttribute('data-seo', 'page');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    return () => {
      const schemaScript = document.querySelector('script[data-seo="page"][type="application/ld+json"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, fullUrl, ogType, imageUrl, imageAlt, author, articlePublishedTime, articleModifiedTime, schema, noIndex]);
};

export const updateSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  author,
  articlePublishedTime,
  articleModifiedTime,
  schema,
  noIndex = false,
}: SEOProps) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  const fullUrl = canonical ? `${siteUrl}${canonical}` : `${siteUrl}${window.location.pathname}`;
  const imageUrl = ogImage || DEFAULT_OG_IMAGE;
  const imageAlt = ogImageAlt || 'Roseville, California — Community Guide';

  document.title = title;

  const updateMeta = (name: string, content: string, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  updateMeta('description', description);
  updateMeta('keywords', keywords);
  updateMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
  
  if (author) {
    updateMeta('author', author);
  }

  updateMeta('og:title', title, true);
  updateMeta('og:description', description, true);
  updateMeta('og:type', ogType, true);
  updateMeta('og:url', fullUrl, true);
  updateMeta('og:image', imageUrl, true);
  updateMeta('og:image:width', '1200', true);
  updateMeta('og:image:height', '630', true);
  updateMeta('og:image:alt', imageAlt, true);
  updateMeta('og:site_name', 'Roseville.org', true);
  updateMeta('og:locale', 'en_US', true);

  if (articlePublishedTime) {
    updateMeta('article:published_time', articlePublishedTime, true);
  }
  if (articleModifiedTime) {
    updateMeta('article:modified_time', articleModifiedTime, true);
  }

  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:title', title);
  updateMeta('twitter:description', description);
  updateMeta('twitter:image', imageUrl);
  updateMeta('twitter:image:alt', imageAlt);
  updateMeta('twitter:site', '@RosevilleOrg');

  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.href = fullUrl;

  const today = new Date().toISOString().split('T')[0];
  updateMeta('last-modified', today);

  if (schema) {
    let schemaScript = document.querySelector('script[data-seo="page"][type="application/ld+json"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-seo', 'page');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schema);
  }
};

// ─── Schema Generators ───────────────────────────────────────────────────────

export const generateWebPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: siteUrl },
  };
};

// Add Organization schema generator
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Roseville.org',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/85b7f1e80330d554f03850e83485dc2a.jpeg',
    description: 'Official visitor and resident guide for Roseville, California',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '5098 Foothills Blvd, Suite 3-402',
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      postalCode: '95747',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 38.7521,
      longitude: -121.2880,
    },
    telephone: '+1-916-238-8515',
    email: 'info@roseville.org',
    sameAs: [
      'https://www.facebook.com/RosevilleOrg',
      'https://twitter.com/RosevilleOrg',
      'https://www.instagram.com/rosevilleorg',
      'https://www.linkedin.com/company/roseville-org',
      'https://www.yelp.com/biz/roseville-org'
    ],
    areaServed: {
      '@type': 'City',
      name: 'Roseville',
      '@id': 'https://en.wikipedia.org/wiki/Roseville,_California',
    },
  };
};

export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Roseville.org',
    description: 'Community guide and local resource directory for Roseville, California',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/85b7f1e80330d554f03850e83485dc2a.jpeg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '5098 Foothills Blvd, Suite 3-402',
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      postalCode: '95747',
      addressCountry: 'US',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 38.7521, longitude: -121.2880 },
    telephone: '+1-916-238-8515',
    areaServed: {
      '@type': 'City',
      name: 'Roseville',
      '@id': 'https://en.wikipedia.org/wiki/Roseville,_California',
    },
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
};

export const generateArticleSchema = (
  headline: string,
  description: string,
  url: string,
  datePublished?: string,
  dateModified?: string,
  authorName?: string,
  imageSrc?: string,
  category?: string,
  wordCount?: number
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  const fullUrl = `${siteUrl}${url}`;
  
  return {
    '@type': 'BlogPosting',
    headline,
    description,
    url: fullUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
    datePublished: datePublished || new Date().toISOString().split('T')[0],
    dateModified: dateModified || new Date().toISOString().split('T')[0],
    author: { '@type': 'Organization', name: authorName || 'Roseville.org' },
    publisher: {
      '@type': 'Organization',
      name: 'Roseville.org',
      url: siteUrl,
      logo: { 
        '@type': 'ImageObject', 
        url: 'https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/85b7f1e80330d554f03850e83485dc2a.jpeg',
        width: 600,
        height: 60,
      },
    },
    ...(imageSrc ? { 
      image: { 
        '@type': 'ImageObject', 
        url: imageSrc,
        width: 1200,
        height: 600,
      } 
    } : {}),
    ...(category ? { articleSection: category } : {}),
    ...(wordCount ? { wordCount } : {}),
    inLanguage: 'en-US',
  };
};

export const generateEventSchema = (
  name: string,
  description: string,
  startDate: string,
  location: string,
  url: string,
  endDate?: string,
  image?: string,
  admission?: string,
  organizer?: string,
  organizerUrl?: string,
  address?: string,
  eventStatus?: string
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  
  // Parse admission to determine offer details
  let offerPrice = '0';
  let offerAvailability = 'https://schema.org/InStock';
  let offerPriceCurrency = 'USD';
  
  if (admission && admission.toLowerCase() !== 'free') {
    const priceMatch = admission.match(/\$(\d+)/);
    if (priceMatch) {
      offerPrice = priceMatch[1];
    }
  }
  
  return {
    '@type': 'Event',
    name,
    description,
    startDate,
    ...(endDate ? { endDate } : {}),
    eventStatus: eventStatus || 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: location,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address || '',
        addressLocality: 'Roseville',
        addressRegion: 'CA',
        postalCode: '95678',
        addressCountry: 'US',
      },
    },
    ...(image ? {
      image: {
        '@type': 'ImageObject',
        url: image,
        width: 1200,
        height: 600,
      }
    } : {}),
    offers: {
      '@type': 'Offer',
      price: offerPrice,
      priceCurrency: offerPriceCurrency,
      availability: offerAvailability,
      url: `${siteUrl}${url}`,
      validFrom: new Date().toISOString().split('T')[0],
    },
    organizer: {
      '@type': 'Organization',
      name: organizer || 'Roseville.org',
      url: organizerUrl || siteUrl,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '5098 Foothills Blvd, Suite 3-402',
        addressLocality: 'Roseville',
        addressRegion: 'CA',
        postalCode: '95747',
        addressCountry: 'US',
      },
    },
    performer: {
      '@type': 'Organization',
      name: organizer || 'Roseville Community',
    },
    url: `${siteUrl}${url}`,
  };
};

export const generateRestaurantSchema = (
  name: string,
  description: string,
  address: string,
  telephone: string,
  url: string,
  priceRange?: string,
  cuisine?: string,
  rating?: number,
  reviewCount?: number,
  menuSections?: Array<{
    category: string;
    items: Array<{ name: string; description: string; price: string }>;
  }>,
  hours?: string
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  
  // Build menu schema if menu data is provided
  const menuSchema = menuSections && menuSections.length > 0 ? {
    hasMenu: {
      '@type': 'Menu',
      name: `${name} Menu`,
      description: `Full menu for ${name}`,
      hasMenuSection: menuSections.map(section => ({
        '@type': 'MenuSection',
        name: section.category,
        hasMenuItem: section.items.map(item => {
          // Extract numeric price from string like "$12" or "$14.99"
          const priceMatch = item.price.match(/\$?(\d+(?:\.\d{2})?)/);
          const priceValue = priceMatch ? priceMatch[1] : '0';
          
          return {
            '@type': 'MenuItem',
            name: item.name,
            description: item.description,
            offers: {
              '@type': 'Offer',
              price: priceValue,
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock'
            }
          };
        })
      }))
    }
  } : {};

  // Parse opening hours if provided
  const openingHoursSpec = hours ? {
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      opens: hours.split('-')[0]?.trim() || '11:00',
      closes: hours.split('-')[1]?.trim() || '22:00'
    }
  } : {};

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name,
    description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      postalCode: '95678',
      addressCountry: 'US',
    },
    telephone,
    url: `${siteUrl}${url}`,
    ...(priceRange ? { priceRange } : {}),
    ...(cuisine ? { servesCuisine: cuisine } : {}),
    ...(rating && reviewCount ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating.toString(),
        reviewCount: reviewCount.toString(),
        bestRating: '5',
        worstRating: '1',
      }
    } : {}),
    ...menuSchema,
    ...openingHoursSpec,
    image: `${siteUrl}${url}`,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 38.7521,
      longitude: -121.2880
    }
  };
};

export const generateJobPostingSchema = (
  title: string,
  description: string,
  hiringOrganization: string,
  addressLocality: string,
  url: string,
  datePosted?: string,
  employmentType?: string
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title,
    description,
    hiringOrganization: { '@type': 'Organization', name: hiringOrganization },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality,
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    url: `${siteUrl}${url}`,
    datePosted: datePosted || new Date().toISOString().split('T')[0],
    ...(employmentType ? { employmentType } : {}),
  };
};

export const generateCollectionPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: siteUrl },
  };
};

export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Roseville.org',
    url: siteUrl,
    description: 'Official visitor and resident guide for Roseville, California',
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/directory?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
});

export const generateNeighborhoodSchema = (
  name: string,
  description: string,
  url: string,
  latitude?: number,
  longitude?: number
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name,
    description,
    url: `${siteUrl}${url}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
    ...(latitude && longitude
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude,
            longitude,
          },
        }
      : {}),
    containedInPlace: {
      '@type': 'City',
      name: 'Roseville',
      '@id': 'https://en.wikipedia.org/wiki/Roseville,_California',
    },
  };
};

export const generateSchoolSchema = (
  name: string,
  description: string,
  address: string,
  telephone: string,
  url: string,
  schoolType?: string
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SITE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name,
    description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
    telephone,
    url: `${siteUrl}${url}`,
    ...(schoolType ? { additionalType: schoolType } : {}),
    areaServed: {
      '@type': 'City',
      name: 'Roseville',
    },
  };
};